import { nanoid } from 'nanoid'

import { generateNonce, generatePKCE, parseJWT } from './utils'

import { WebCryptoStorage } from './utils/storage.crypto'
import { SessionStorage } from '../../utils/storage'

import { OIDC_TRANSACTION_KEY } from './constants'
import { TokenData, ClientMode, SessionState } from './constants/types'


import {
  AuthenticationError,
  InvalidSessionStateError,
  LoginRequiredError,
  OidcCallbackParamsError,
  // OriginIsNotAllowedError
} from '../../errors'

export type StateConfig = {
  authority: string
  client_id: string | number
  callback_url?: string
  origin?: string
  authorization_endpoint: string
  end_session_endpoint: string
  response_type: string
  response_mode: string,
  scope: string,
  mode?: ClientMode,
  cryptoStorage: WebCryptoStorage
}

export type IdToken = {
  amr: string[]
  at_hash: string
  auth_time: number
  aud: string
  email: string
  email_verified: boolean
  exp: number
  iat: number
  idp: string
  iss: string
  name: string
  nbf: number
  nonce?: string
  preferred_username: string
  s_hash: string
  sid: string
  sub: string | number
}

export type AccessToken = {
  amr: string[]
  aud: string[]
  auth_time: number
  iss: string
  client_id: string
  email: string
  sub: string
  jti: string
  sid: string
  exp: number
  iat: number
  nbf: number
  scope: Array<string>
  legacy_uguid: string
  legacy_token: string
}

export type StateData = {
  code_challenge?: string
  code_verifier?: string,
  session_state?: string,
  code?: string,
  nonce?: string,
  state?: string
}

export type ReadyState = {
  data: StateData
  token: TokenData
}

export type TokenDate = 'expiredAt' | 'notBeforeAt' | 'issuedAt'

export type Prompt = 'none' | 'login'

export type Transaction = {
  id: number
  mode: ClientMode
  path?: string
  code_verifier?: string
  state?: string
}

export type Workflow = 'none' | 'login' | 'register'

const MissingAuthenticationSessionError = new AuthenticationError('No authentication session.')

export class State {
  readonly config: StateConfig

  public id: string
  public jwt?: AccessToken
  public jwtId?: IdToken
  public token?: TokenData
  public data: StateData = {}
  public transaction?: Transaction

  get isLoggedIn() {
    return !!this.id && !!this.data.session_state && !!this.data.state && !!this.token
  }

  constructor(config: StateConfig) {
    this.id = nanoid()
    this.config = config
  }

  public createSessionTransaction(mode: ClientMode, path = location.pathname + location.search) {
    this.transaction = {
      id: Date.now(),
      mode,
      path,
      code_verifier: this.data.code_verifier,
      state: this.data.state || this.id
    }

    SessionStorage.setItem(OIDC_TRANSACTION_KEY, this.transaction)
  }

  public handlePopupSession() {
    const q = new URLSearchParams(location.search)
    this.data = {
      code: q.get('code') as string,
      state: q.get('state') as string,
      session_state: q.get('session_state') as string
    }
    this.id = this.data.state as string
  }

  public closeSessionTransaction() {
    SessionStorage.removeItem(OIDC_TRANSACTION_KEY)
  }

  public finishSessionTransaction() {
    const transaction = SessionStorage.getItem(OIDC_TRANSACTION_KEY) as Transaction

    if (transaction) {
      const q = new URLSearchParams(location.search)
      const state = q.get('state') as string

      if (state && state === transaction.state) {
        this.transaction = transaction
        this.data = {
          code_verifier: transaction.code_verifier,
          code: q.get('code') as string,
          state,
          session_state: q.get('session_state') as string
        }

        this.id = state

        this.closeSessionTransaction()
      } else {
        throw new OidcCallbackParamsError
      }
    } else {
      throw new InvalidSessionStateError
    }
  }


  /**
   * Generate URL for SSO login popup window.
   * All params are required.
   * @param prompt - Could contain `none` or `login` values.
   * To disable SSO from a client perspective is to send prompt=`login` on the request query string.
   * This disables all usage of SSO cookies and forces a new authentication for all factors required.
   * P.S. To open registration page instead of login need to pass `ui_flow=register` param
   */
  public async createURL(display: string, workflow?: Workflow, prompt?: Prompt, origin = location.host) {
    const { code_challenge, code_challenge_method, code_verifier } = await generatePKCE()
    const nonce = await generateNonce()

    const url = new URL(this.config.authorization_endpoint)
    const queryParams = [
      ['client_id', this.config.client_id],
      ['redirect_uri', this.config.callback_url],
      ['response_type', this.config.response_type],
      ['response_mode', this.config.response_mode],
      ['scope', this.config.scope],
      ['state', this.id],
      ['code_challenge', code_challenge],
      ['code_challenge_method', code_challenge_method],
      ['origin', origin],
      ['nonce', nonce],
      ['display', display],
      workflow && ['ui_flow', workflow],
      prompt && ['prompt', prompt as string]
    ].filter(Boolean)

    this.data.nonce = nonce
    this.data.code_verifier = code_verifier
    this.data.code_challenge = code_challenge

    queryParams.forEach(pair => {
      const [key, value] = pair as string[]
      url.searchParams.append(key, String(value))
    })

    return { url: url.href, code_challenge, code_verifier, state: this.id, nonce }
  }

  public update(state: ReadyState) {
    this.data = state.data
    this.token = state.token
    this.jwt = parseJWT(state.token.access_token) as unknown as AccessToken
    this.id = state.data.state as string

    if (state.token.id_token && !state.data.nonce) {
      const idToken = parseJWT<IdToken>(state.token.id_token)

      idToken && (this.jwtId = idToken)
      idToken?.nonce && (this.data.nonce = idToken.nonce)
    }
  }

  public async load(): Promise<State> {
    await this.config.cryptoStorage.setup()

    if (!this.config.cryptoStorage.isEmpty) {
      try {
        const state: ReadyState = await this.config.cryptoStorage.read()

        if (!state.token) {
          throw MissingAuthenticationSessionError
        }

        this.update(state)
      } catch {
        throw MissingAuthenticationSessionError
      }
    } else {
      throw MissingAuthenticationSessionError
    }

    return this
  }

  public save() {
    const token = this.token as TokenData

    return this.config.cryptoStorage.save({
      data: {
        session_state: this.data.session_state,
        state: this.data.state
      },
      token: {
        access_token: token.access_token,
        refresh_token: token.refresh_token,
        id_token: token.id_token,
        expires_in: token.expires_in
      }
    })
  }

  public clear(): void {
    this.closeSessionTransaction()
    return this.config.cryptoStorage.clear()
  }

  public tokenDate(type: TokenDate) {
    if (!this.jwt) return null

    switch (type) {
      case 'expiredAt':
        return this.jwt.exp ? Number(this.jwt.exp) * 1000 : null
      case 'notBeforeAt':
        return this.jwt.nbf ? Number(this.jwt.nbf) * 1000 : null
      case 'issuedAt':
        return this.jwt.iat ? Number(this.jwt.iat) * 1000 : null
      default:
        return null
    }
  }

  public validate() {
    return this.config.cryptoStorage.validate()
  }

  public test(data: SessionState) {
    const { state, error } = data

    if (error === 'login_required') {
      throw new LoginRequiredError('Login required.')
    } else if (error) {
      console.error(error)
      throw new Error(error)
    } else if (state !== this.id) {
      throw new InvalidSessionStateError
    }
  }
}
